import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { FiUser, FiMapPin, FiMail, FiPhone, FiGlobe } from 'react-icons/fi';

const AboutSection = styled.section`
  padding: 6rem 2rem;
  background: transparent;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0.8rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #00ff00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  color:rgb(111, 226, 83);
  font-size: 1.2rem;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 3rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 2.5rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    gap: 1.5rem;
  }
`;

const AboutContent = styled(motion.div)`
  color: #cbd5e1;
  line-height: 1.8;
`;

const AboutText = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  color: #e2e8f0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.7;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.8;
  }
`;

const HighlightText = styled.span`
  color: #00ff00;
  font-weight: 600;
`;

const PersonalInfo = styled(motion.div)`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(0, 255, 0, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.2rem;
    border-radius: 12px;
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #ffffff;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoItem = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: rgba(0, 255, 0, 0.05);
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 255, 0, 0.1);
    transform: translateX(5px);
  }
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(45deg, #00ff00, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 0.9rem;
  color: #94a3b8;
  margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
  font-weight: 500;
  color: #ffffff;
`;

const SkillsPreview = styled(motion.div)`
  margin-top: 2rem;
`;

const SkillsTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`;

const SkillTag = styled(motion.div)`
  background: rgba(0, 255, 0, 0.15);
  border: 2px solid rgba(0, 255, 0, 0.5);
  color: #00ff00;
  padding: 0.75rem 1.25rem;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: 0 0 15px rgba(0, 255, 0, 0.2);
  text-shadow: 0 0 5px rgba(0, 255, 0, 0.5);

  &:hover {
    background: rgba(0, 255, 0, 0.25);
    border-color: rgba(0, 255, 0, 0.8);
    transform: translateY(-3px);
    box-shadow: 0 0 25px rgba(0, 255, 0, 0.4);
  }
`;

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const personalInfo = [
    { icon: <FiUser />, label: 'Name', value: 'Anas Naveed Butt' },
    { icon: <FiMapPin />, label: 'Location', value: 'Sargodha, Pakistan' },
    { icon: <FiMail />, label: 'Email', value: 'buttanas813@gmail.com' },
    { icon: <FiPhone />, label: 'Phone', value: '+92 3427492742' },
    { icon: <FiGlobe />, label: 'Languages', value: 'English, Urdu' }
  ];

  const previewSkills = [
    'React', 'Next.js', 'JavaScript', 'TypeScript', 'Node.js', 'MongoDB', 'Python', 'AI/ML', 'MERN'
  ];

  return (
    <AboutSection id="about" ref={ref}>
      <Container>
        <SectionTitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          About Me
        </SectionTitle>
        
        <SectionSubtitle
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Get to know me better and understand my journey in the world of technology
        </SectionSubtitle>

        <ContentGrid>
          <AboutContent
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <AboutText>
              I'm a driven and adaptable <HighlightText>Computer Science student</HighlightText> with a strong foundation in C, C++, and Assembly, currently specializing in web development and exploring cutting-edge fields in machine learning, large language models (LLMs), and artificial intelligence.
            </AboutText>
            
            <AboutText>
              I'm skilled in creating efficient, user-focused applications and passionate about integrating <HighlightText>AI innovations</HighlightText> into modern software solutions. I am committed to continuous learning, collaborative problem-solving, and making meaningful contributions to forward-thinking development projects.
            </AboutText>
            
            <AboutText>
              Currently working as a <HighlightText>Web Developer at Next Edge Solutions</HighlightText>, I design and develop innovative, high-performance digital solutions that empower businesses to achieve their goals. I leverage expertise in modern web development technologies to deliver efficient, scalable, and user-focused applications.
            </AboutText>

            <AboutText>
              Additionally, I am active in my university community as an <HighlightText>Officer of Security & Reception for the SOFTEC Society at FAST-NU</HighlightText> (Aug 2024 – Present), where I help organize and coordinate major campus events.
            </AboutText>
            
            <AboutText>
              When I'm not coding, you can find me playing cricket, badminton, or video games. I'm team-oriented, analytical, and driven with a strong interest in software innovation.
            </AboutText>

            <SkillsPreview
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <SkillsTitle>Key Skills</SkillsTitle>
              <SkillsGrid>
                {previewSkills.map((skill, index) => (
                  <SkillTag
                    key={skill}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.4, delay: 1 + index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {skill}
                  </SkillTag>
                ))}
              </SkillsGrid>
            </SkillsPreview>
          </AboutContent>

          <PersonalInfo
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <InfoTitle>Personal Information</InfoTitle>
            <InfoList>
              {personalInfo.map((info, index) => (
                <InfoItem
                  key={info.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  whileHover={{ x: 5 }}
                >
                  <InfoIcon>
                    {info.icon}
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>{info.label}</InfoLabel>
                    <InfoValue>{info.value}</InfoValue>
                  </InfoContent>
                </InfoItem>
              ))}
            </InfoList>
          </PersonalInfo>
        </ContentGrid>
      </Container>
    </AboutSection>
  );
};

export default About;
